import React, { useEffect, useState, useMemo } from 'react';
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SidebarProvider, useSidebar } from '../../contexts/SidebarContext';
import { FiUsers, FiUser, FiBook, FiLayers, FiList, FiFileText, FiLogOut, FiCheckCircle, FiPlus, FiEye, FiClipboard, FiHeadphones, FiHome, FiChevronDown, FiChevronRight, FiMenu, FiX, FiSidebar } from 'react-icons/fi';
import StudentsPage from './Students/StudentsPage';
import StudentProfile from './Students/StudentProfile';
import StudentGroupsPage from './StudentGroups/StudentGroupsPage';
import AreasPage from './Areas/AreasPage';
import LevelsPage from './Levels/LevelsPage';
import LessonsPage from './Lessons/LessonsPage';
import TextsPage from './Texts/TextsPage';
import FormulasPage from './Formulas/FormulasPage';
import CreateLessonPage from './CreateLesson/CreateLessonPage';
import CreateLessonPageBlocks from './CreateLesson/CreateLessonPageBlocks';
import ViewLessonsPage from './ViewLessons/ViewLessonsPage';
import TestsPage from './Tests/TestsPage';
import CreateTestPageBlocks from './Tests/CreateTestPageBlocks';
import TestResultsDetailPage from './Tests/TestResultsDetailPage';
import AudioCheckingPage from './Checking/AudioCheckingPage';
import TestResultsPage from './Checking/TestResultsPage';
import TestBlocksCheckingPage from './Checking/TestBlocksCheckingPage';
import TestBlocksCheckingDetailPage from './Checking/TestBlocksCheckingDetailPage';
import HomeworkCheckingPage from './Checking/HomeworkCheckingPage';
import HomeworkCheckingDetailPage from './Checking/HomeworkCheckingDetailPage';
import ExercisesCheckingPage from './Checking/ExercisesCheckingPage';
import AdminLessonView from './ViewLesson/AdminLessonView';
import HomeworkPage from './Homework/HomeworkPage';
import CreateHomeworkPage from './Homework/CreateHomeworkPage';
import ViewHomeworkPage from './Homework/ViewHomeworkPage';
import MaterialsPage from './Materials/MaterialsPage';
import CreateMaterialPage from './Materials/CreateMaterialPage';
import CreateLessonMaterialPage from './Materials/CreateLessonMaterialPage';
import EditLessonMaterialPage from './Materials/EditLessonMaterialPage';
import TeacherMaterialsPage from './Materials/TeacherMaterialsPage';
import TeachersPage from './Teachers/TeachersPage';
import './AdminDashboard.css';

const AdminDashboardContent = () => {
    const { user, logout } = useAuth();
    const isAdminUser = user?.role === 'admin';
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedMenu, setExpandedMenu] = useState({});
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth <= 768);
    const { toggleSidebar } = useSidebar();

    useEffect(() => {
        const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isActive = (path, exact = false) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };
    
    const toggleMenu = (key) => {
        setExpandedMenu(prev => {
            if (prev[key]) {
                return { ...prev, [key]: false };
            }
            return { [key]: true };
        });
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
        setExpandedMenu({});
    };

    const menuStructure = useMemo(() => {
        const lessonsSubmenu = isAdminUser
            ? [
                { path: '/admin/create-lesson-blocks', icon: <FiPlus />, label: 'Создать урок' },
                { path: '/admin/view-lessons', icon: <FiEye />, label: 'Все уроки' },
            ]
            : [{ path: '/admin/view-lessons', icon: <FiEye />, label: 'Все уроки' }];

        const items = [
            { path: '/admin', icon: <FiHome />, label: 'Главная', exact: true },
            ...(isAdminUser ? [{ path: '/admin/teachers', icon: <FiUser />, label: 'Преподаватели' }] : []),
            { path: '/admin/students', icon: <FiUsers />, label: 'Ученики' },
            { path: '/admin/student-groups', icon: <FiUsers />, label: 'Группы' },
            {
                key: 'lessons',
                icon: <FiBook />,
                label: 'Уроки',
                submenu: lessonsSubmenu,
            },
            { path: '/admin/homework', icon: <FiClipboard />, label: 'ДЗ' },
            { path: '/admin/materials', icon: <FiFileText />, label: 'Материалы' },
            ...(isAdminUser ? [{ path: '/admin/teacher-materials', icon: <FiBook />, label: 'Методические' }] : []),
            { path: '/admin/tests', icon: <FiFileText />, label: 'Тесты' },
            {
                key: 'checking',
                icon: <FiCheckCircle />,
                label: 'Проверка',
                submenu: [
                    { path: '/admin/checking/homework', icon: <FiClipboard />, label: 'ДЗ' },
                    { path: '/admin/checking/exercises', icon: <FiCheckCircle />, label: 'Упражнения уроков' },
                    { path: '/admin/checking/audio', icon: <FiHeadphones />, label: 'Аудио' },
                    { path: '/admin/checking/test-blocks', icon: <FiFileText />, label: 'Тесты' },
                ],
            },
            ...(isAdminUser
                ? [
                      {
                          key: 'structure',
                          icon: <FiLayers />,
                          label: 'База',
                          submenu: [
                              { path: '/admin/areas', icon: <FiLayers />, label: 'Области' },
                              { path: '/admin/levels', icon: <FiList />, label: 'Уровни' },
                          ],
                      },
                      { path: '/admin/formulas', icon: <FiCheckCircle />, label: 'Формулы' },
                  ]
                : []),
        ];
        return items;
    }, [isAdminUser]);

    return (
        <div className="admin-dashboard">
            {/* Оверлей для мобильного меню */}
            {mobileMenuOpen && (
                <div 
                    className="admin-mobile-overlay active" 
                    onClick={closeMobileMenu}
                />
            )}

            {/* Горизонтальное верхнее меню */}
            <nav className="admin-top-nav">
                <div className="top-nav-left">
                    <button 
                        className="admin-mobile-menu-button" 
                        onClick={toggleMobileMenu}
                        aria-label="Меню"
                    >
                        {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                    </button>

                    {/* Кнопка для бокового меню на страницах с sidebar */}
                    {isMobileViewport && (location.pathname.includes('/view-lessons') || location.pathname.includes('/lessons/') && location.pathname.includes('/view')) && (
                        <button 
                            className="sidebar-toggle-button" 
                            onClick={toggleSidebar}
                            aria-label="Боковое меню"
                            title="Открыть боковое меню"
                        >
                            <FiSidebar size={20} />
                        </button>
                    )}
                </div>

                <div className="top-nav-brand">
                    <FiBook size={16} />
                    Speaking Drive
                    {user?.role === 'teacher' && (
                        <span className="admin-role-badge">Преподаватель</span>
                    )}
                </div>

                <div className={`top-nav-links ${mobileMenuOpen ? 'admin-mobile-menu-open' : ''}`}>
                    {menuStructure.map((item) => {
                        if (item.submenu) {
                            const isAnySubmenuActive = item.submenu.some(sub => isActive(sub.path));
                            return (
                                <div key={item.key} className="nav-dropdown">
                                    <button
                                        className={`top-nav-link ${isAnySubmenuActive ? 'active' : ''}`}
                                        onClick={() => toggleMenu(item.key)}
                                    >
                                        {item.icon}
                                        <span>{item.label}</span>
                                        {expandedMenu[item.key] ? <FiChevronDown /> : <FiChevronRight />}
                                    </button>
                                    {expandedMenu[item.key] && (
                                        <div className="dropdown-menu">
                                            {item.submenu.map(sub => (
                                                <Link
                                                    key={sub.path}
                                                    to={sub.path}
                                                    className={`dropdown-item ${isActive(sub.path) ? 'active' : ''}`}
                                                    onClick={closeMobileMenu}
                                                >
                                                    {sub.icon}
                                                    <span>{sub.label}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`top-nav-link ${isActive(item.path, item.exact) ? 'active' : ''}`}
                                onClick={closeMobileMenu}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </div>

                <div className="top-nav-user">
                    <span className="user-name">{user?.username}</span>
                    <button onClick={handleLogout} className="btn-logout-top">
                        <FiLogOut />
                        <span className="btn-logout-text">Выйти</span>
                    </button>
                </div>
            </nav>

            {/* Основной контент */}
            <main className="admin-content-wrapper">
                <Routes>
                    <Route path="/" element={<AdminHome />} />
                    <Route
                        path="/teachers"
                        element={isAdminUser ? <TeachersPage /> : <Navigate to="/admin" replace />}
                    />
                    <Route path="/students/*" element={<StudentsPage />} />
                    <Route path="/student-groups" element={<StudentGroupsPage />} />
                    <Route path="/student/:studentId" element={<StudentProfile />} />
                    <Route path="/create-lesson" element={<CreateLessonPage />} />
                    <Route path="/lessons/edit/:lessonId" element={<CreateLessonPage />} />
                    <Route path="/create-lesson-blocks" element={<CreateLessonPageBlocks />} />
                    <Route path="/lessons-blocks/edit/:lessonId" element={<CreateLessonPageBlocks />} />
                    <Route path="/view-lessons" element={<ViewLessonsPage />} />
                    <Route path="/lessons/:lessonId/view" element={<AdminLessonView />} />
                    <Route path="/homework" element={<HomeworkPage />} />
                    <Route path="/homework/create" element={<CreateHomeworkPage />} />
                    <Route path="/homework/:id" element={<ViewHomeworkPage />} />
                    <Route path="/homework/:id/edit" element={<CreateHomeworkPage />} />
                    <Route path="/materials" element={<MaterialsPage />} />
                    <Route path="/materials/create" element={<CreateMaterialPage />} />
                    <Route path="/materials/:id/edit" element={<CreateMaterialPage />} />
                    <Route path="/materials/create-lesson" element={<CreateLessonMaterialPage />} />
                    <Route path="/materials/:materialId/lesson/:lessonId/edit-lesson-material" element={<EditLessonMaterialPage />} />
                    <Route path="/teacher-materials" element={<TeacherMaterialsPage />} />
                    <Route path="/tests" element={<TestsPage />} />
                    <Route path="/tests/create" element={<CreateTestPageBlocks />} />
                    <Route path="/tests/:id/edit" element={<CreateTestPageBlocks />} />
                    <Route path="/tests/:id/results" element={<TestResultsDetailPage />} />
                    <Route path="/checking/audio" element={<AudioCheckingPage />} />
                    <Route path="/checking/test-results" element={<TestResultsPage />} />
                    <Route path="/checking/test-blocks" element={<TestBlocksCheckingPage />} />
                    <Route path="/checking/test-blocks/:resultId" element={<TestBlocksCheckingDetailPage />} />
                    <Route path="/checking/homework" element={<HomeworkCheckingPage />} />
                    <Route path="/checking/homework/:id" element={<HomeworkCheckingDetailPage />} />
                    <Route path="/checking/exercises" element={<ExercisesCheckingPage />} />
                    <Route path="/areas/*" element={<AreasPage />} />
                    <Route path="/levels/*" element={<LevelsPage />} />
                    <Route path="/lessons/*" element={<LessonsPage />} />
                    <Route path="/texts/*" element={<TextsPage />} />
                    <Route path="/formulas/*" element={<FormulasPage />} />
                </Routes>
            </main>
        </div>
    );
};

const AdminHome = () => {
    const { user } = useAuth();
    const isAdminUser = user?.role === 'admin';

    return (
        <div className="container">
            <div className="page-header">
                <h1>Добро пожаловать в админ-панель</h1>
                <p>
                    {isAdminUser
                        ? 'Выберите раздел для начала работы'
                        : 'Доступны ваши ученики, уроки по назначенным уровням, ДЗ и проверка работ'}
                </p>
            </div>

            <div className="dashboard-cards">
                {isAdminUser && (
                    <Link to="/admin/teachers" className="dashboard-card">
                        <FiUser size={40} color="#2f54eb" />
                        <h3>Преподаватели</h3>
                        <p>Доступ коллег к ученикам и уровням</p>
                    </Link>
                )}

                <Link to="/admin/students" className="dashboard-card">
                    <FiUsers size={40} color="#4a90e2" />
                    <h3>Ученики</h3>
                    <p>Управление учениками и их прогрессом</p>
                </Link>

                {isAdminUser && (
                    <Link to="/admin/create-lesson-blocks" className="dashboard-card" style={{ border: '2px solid #52c41a' }}>
                        <FiPlus size={40} color="#52c41a" />
                        <h3>Создать урок</h3>
                        <p>Создание урока из блоков</p>
                    </Link>
                )}

                <Link to="/admin/view-lessons" className="dashboard-card">
                    <FiEye size={40} color="#7b68ee" />
                    <h3>Все уроки</h3>
                    <p>Просмотр и работа с уроками</p>
                </Link>

                <Link to="/admin/homework" className="dashboard-card">
                    <FiClipboard size={40} color="#1890ff" />
                    <h3>Домашние задания</h3>
                    <p>Создание и управление ДЗ для студентов</p>
                </Link>

                <Link to="/admin/materials" className="dashboard-card">
                    <FiFileText size={40} color="#13c2c2" />
                    <h3>Материалы</h3>
                    <p>Дополнительные материалы для изучения</p>
                </Link>

                {isAdminUser && (
                    <Link to="/admin/teacher-materials" className="dashboard-card">
                        <FiBook size={40} color="#722ed1" />
                        <h3>Методические материалы</h3>
                        <p>Планы уроков, ответы и заметки</p>
                    </Link>
                )}

                <Link to="/admin/tests" className="dashboard-card">
                    <FiClipboard size={40} color="#fa8c16" />
                    <h3>Тестирование</h3>
                    <p>Создание и управление независимыми тестами</p>
                </Link>

                {isAdminUser && (
                    <>
                        <Link to="/admin/areas" className="dashboard-card">
                            <FiLayers size={40} color="#722ed1" />
                            <h3>Области обучения</h3>
                            <p>Создание и настройка областей (General English, ЕГЭ и т.д.)</p>
                        </Link>

                        <Link to="/admin/levels" className="dashboard-card">
                            <FiList size={40} color="#52c41a" />
                            <h3>Уровни</h3>
                            <p>Управление уровнями (A1, A2, B1 и т.д.)</p>
                        </Link>

                        <Link to="/admin/formulas" className="dashboard-card">
                            <FiCheckCircle size={40} color="#faad14" />
                            <h3>Речевые формулы</h3>
                            <p>Отслеживание прогресса учеников по формулам</p>
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
};

const AdminDashboard = () => {
    return (
        <SidebarProvider>
            <AdminDashboardContent />
        </SidebarProvider>
    );
};

export default AdminDashboard;
